/**
 * 360° photos in the browser (1.173.0, #154): reading the camera's flag
 * before a photo is scaled down, and writing it again into the scaled one.
 *
 * **The server decides** whether a photo is shown as a sphere
 * (`Panorama::isFullSphere()`, and only for the trip's author). The browser
 * asks the same question for two reasons of its own: a sphere is scaled to
 * its own edge (`pano_max_edge`), not to the one of a flat photo, and the
 * redrawn JPEG would lose the XMP block that carries the flag -- without it
 * the server would see a flat photo. Two readers, one per language, held at
 * the same cases (`tools/pruefstand/panofaelle.json`); a change here is a
 * change there.
 */

const XMP_HEADER = 'http://ns.adobe.com/xap/1.0/\0'
const APP1 = 0xe1
/** How far from 2:1 a full sphere may be, as in `Panorama::ASPECT_SLACK`. */
const ASPECT_SLACK = 0.02

/** The text of the first XMP block of a JPEG, or null. Stops at the image data. */
function xmpOf(jpeg: Uint8Array): string | null {
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return null
  const header = new TextEncoder().encode(XMP_HEADER)
  const byte = (i: number) => jpeg[i] ?? 0
  let at = 2
  while (at + 4 <= jpeg.length) {
    if (byte(at) !== 0xff) return null
    const marker = byte(at + 1)
    if (marker === 0xda || marker === 0xd9) return null
    const size = (byte(at + 2) << 8) | byte(at + 3)
    if (size < 2 || at + 2 + size > jpeg.length) return null
    if (marker === APP1 && header.every((byte, i) => jpeg[at + 4 + i] === byte)) {
      return new TextDecoder().decode(jpeg.subarray(at + 4 + header.length, at + 2 + size))
    }
    at += 2 + size
  }
  return null
}

/** A GPano field, written as attribute or as element. */
function field(xmp: string, name: string): string | null {
  const attribute = new RegExp(`GPano:${name}\\s*=\\s*["']([^"']*)["']`).exec(xmp)
  if (attribute) return (attribute[1] ?? '').trim()
  const element = new RegExp(`<GPano:${name}>\\s*([^<]*?)\\s*</GPano:${name}>`).exec(xmp)
  return element ? (element[1] ?? '') : null
}

/** The same three conditions as `Panorama::isFullSphere()`. */
export function isFullSphere(jpeg: Uint8Array, width: number, height: number): boolean {
  if (height <= 0 || Math.abs(width / height - 2) > 2 * ASPECT_SLACK) return false
  const xmp = xmpOf(jpeg)
  if (xmp === null || (field(xmp, 'ProjectionType') ?? '').toLowerCase() !== 'equirectangular') return false
  for (const [part, full] of [
    ['CroppedAreaImageWidthPixels', 'FullPanoWidthPixels'],
    ['CroppedAreaImageHeightPixels', 'FullPanoHeightPixels'],
  ] as const) {
    const cropped = field(xmp, part)
    const whole = field(xmp, full)
    if (cropped !== null && whole !== null && Number(whole) > 0
      && Number(cropped) < Number(whole) * (1 - ASPECT_SLACK)) return false
  }
  return true
}

/**
 * The scaled photo with a fresh XMP block that says what it is.
 *
 * **Written new, not copied from the original.** The camera's block names its
 * own pixel sizes (`FullPanoWidthPixels="6080"`), and after scaling they would
 * be wrong; a viewer that trusts them would draw a partial sphere. The block
 * stands behind the EXIF block (`keepExif()` has put it there), where cameras
 * put it too.
 */
export function withSphereXmp(jpeg: Uint8Array, width: number, height: number): Uint8Array<ArrayBuffer> {
  const xmp = '<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>'
    + '<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">'
    + '<rdf:Description rdf:about="" xmlns:GPano="http://ns.google.com/photos/1.0/panorama/"'
    + ' GPano:UsePanoramaViewer="True" GPano:ProjectionType="equirectangular"'
    + ` GPano:FullPanoWidthPixels="${width}" GPano:FullPanoHeightPixels="${height}"`
    + ` GPano:CroppedAreaImageWidthPixels="${width}" GPano:CroppedAreaImageHeightPixels="${height}"`
    + ' GPano:CroppedAreaLeftPixels="0" GPano:CroppedAreaTopPixels="0"/>'
    + '</rdf:RDF></x:xmpmeta><?xpacket end="w"?>'
  const payload = new TextEncoder().encode(XMP_HEADER + xmp)
  const segment = new Uint8Array(4 + payload.length)
  segment.set([0xff, APP1, (payload.length + 2) >> 8, (payload.length + 2) & 0xff])
  segment.set(payload, 4)

  // Behind SOI and every APP0/APP1 at the front (JFIF, EXIF).
  let at = 2
  const byte = (i: number) => jpeg[i] ?? 0
  while (at + 4 <= jpeg.length && byte(at) === 0xff && (byte(at + 1) === 0xe0 || byte(at + 1) === APP1)) {
    at += 2 + ((byte(at + 2) << 8) | byte(at + 3))
  }
  const out = new Uint8Array(jpeg.length + segment.length)
  out.set(jpeg.subarray(0, at))
  out.set(segment, at)
  out.set(jpeg.subarray(at), at + segment.length)
  return out
}
